import { Link } from "wouter";
import { Mail, MapPin, Phone, MessageCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLocale } from "@/lib/locale";

export function Footer() {
  const { locale, localePath } = useLocale();
  const copy = {
    it: { tagline: "Il gestionale per campeggi e villaggi turistici progettato per semplificare ogni aspetto della tua struttura ricettiva all'aperto.", product: "Prodotto", all: "Tutte le funzionalità", alloggiati: "Invio Alloggiati Web", access: "Controllo Accessi Sbarra", resources: "Risorse", updates: "Novità", contact: "Contattaci", terms: "Termini di Servizio", cookies: "Cookie Policy", contacts: "Contatti", made: "Progettato e realizzato in Italia" },
    en: { tagline: "The management software for campsites and holiday villages, designed to simplify every aspect of your outdoor hospitality business.", product: "Product", all: "All features", alloggiati: "Alloggiati Web submission", access: "Barrier Access Control", resources: "Resources", updates: "Updates", contact: "Contact us", terms: "Terms of Service", cookies: "Cookie Policy", contacts: "Contact", made: "Designed and built in Italy" },
    de: { tagline: "Die Managementsoftware für Campingplätze und Feriendörfer – für eine einfache Verwaltung Ihres gesamten Outdoor-Gastbetriebs.", product: "Produkt", all: "Alle Funktionen", alloggiati: "Alloggiati-Web-Übermittlung", access: "Schrankenzugangskontrolle", resources: "Ressourcen", updates: "Neuigkeiten", contact: "Kontakt", terms: "Nutzungsbedingungen", cookies: "Cookie-Richtlinie", contacts: "Kontakt", made: "In Italien konzipiert und entwickelt" },
    hr: { tagline: "Upravljački softver za kampove i turistička naselja koji pojednostavljuje svaki aspekt vašeg smještaja na otvorenom.", product: "Proizvod", all: "Sve funkcionalnosti", alloggiati: "Slanje Alloggiati Web podataka", access: "Kontrola pristupa rampi", resources: "Resursi", updates: "Novosti", contact: "Kontaktirajte nas", terms: "Uvjeti korištenja", cookies: "Pravila o kolačićima", contacts: "Kontakt", made: "Dizajnirano i razvijeno u Italiji" },
  }[locale];
  const link = (path: string) => localePath(path);
  return (
    <footer className="bg-primary text-primary-foreground pt-16 pb-8">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          
          <div className="flex flex-col gap-4">
            <Link href={link("/")} className="hover:opacity-90 transition-opacity inline-flex">
              <Logo variant="light" />
            </Link>
            <p className="text-primary-foreground/70 text-sm mt-2 leading-relaxed">
              {copy.tagline}
            </p>
          </div>

          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{copy.product}</h3>
            <ul className="flex flex-col gap-3">
              <li>
                <Link href={link("/funzionalita")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm">
                  {copy.all}
                </Link>
              </li>
              <li>
                <Link href={link("/alloggiati-web")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm">
                  {copy.alloggiati}
                </Link>
              </li>
              <li>
                <Link href={link("/prenotazioni-online")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm">
                  Booking Engine
                </Link>
              </li>
              <li>
                <Link href={link("/controllo-accessi-sbarra")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm" data-testid="footer-link-controllo-sbarra">
                  {copy.access}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{copy.resources}</h3>
            <ul className="flex flex-col gap-3">
              <li>
                <Link href={link("/contatti")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm">
                  {copy.contact}
                </Link>
              </li>
              <li>
                <Link href={link("/novita")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm" data-testid="footer-link-updates">
                  {copy.updates}
                </Link>
              </li>
              <li>
                <Link href={link("/privacy")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link href={link("/termini")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm">
                  {copy.terms}
                </Link>
              </li>
              <li>
                <Link href={link("/cookie")} className="text-primary-foreground/70 hover:text-accent transition-colors text-sm">
                  {copy.cookies}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-heading font-bold text-lg mb-4">{copy.contacts}</h3>
            <ul className="flex flex-col gap-4 text-sm text-primary-foreground/70">
              <li className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-accent shrink-0" />
                <span>info@seosoft.it</span>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-accent shrink-0" />
                <a href="tel:+3908831970316" className="hover:text-accent transition-colors">+39 0883 1970316</a>
              </li>
              <li className="flex items-start gap-3">
                <MessageCircle className="w-5 h-5 text-accent shrink-0" />
                <a href="https://wa.me/393382989310" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">WhatsApp: +39 338 298 9310</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-accent shrink-0" />
                <span>Via Gran Bretagna 44,<br/>76125 Trani (BT)</span>
              </li>
            </ul>
          </div>

        </div>

        <div className="pt-8 border-t border-primary-foreground/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-primary-foreground/70 text-sm">
            &copy; {new Date().getFullYear()} OVVO Camping.
            Software gestionale sviluppato da{" "}
            <a
              href="https://seosoft.it"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(16,72%,74%)] hover:text-[hsl(16,80%,82%)] transition-colors underline underline-offset-2"
            >
              SeoSoft
            </a>
            .
          </p>
          <div className="flex items-center gap-5 text-sm flex-wrap justify-center">
            <a
              href="https://seosoft.it"
              target="_blank"
              rel="noopener noreferrer"
              title="SeoSoft — Sviluppo Software Gestionale"
              className="text-primary-foreground/70 hover:text-[hsl(16,72%,74%)] transition-colors"
            >
              SeoSoft
            </a>
            <span className="text-primary-foreground/20">·</span>
           <span className="text-primary-foreground/70">{copy.made}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
